import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';

const gridStyles = {
  display: 'grid',
  gap: 2,
  gridTemplateColumns: '16px 1fr 42px',
  width: 1,
};

const CompartmentsForm = ({
  compartments,
  onAddCompartment,
  onUpdateCompartment,
  onRemoveCompartment,
  title,
}) => {
  const [validation, setValidation] = useState({});

  const validate = R.cond([
    [R.isEmpty, R.always('Capacity required')],
    [R.gt(1), R.always('Capacity must be greater than 0')],
    [R.T, R.always(undefined)],
  ]);

  const handleChange = (index, value) => {
    const removeNonDigits = R.replace(/[^0-9]/g, '');
    const sanitized = R.compose(
      x => x.toLocaleString(),
      R.defaultTo(''),
      parseInt,
      removeNonDigits
    )(value);
    onUpdateCompartment(index, sanitized);
    setValidation(state => R.assoc(index, validate(removeNonDigits(sanitized)), state));
  };

  return (
    <Box sx={gridStyles}>
      {title && <Typography sx={{ gridColumn: '1/4', mb: 2 }}>{title}</Typography>}
      {compartments.map(({ capacityInGallons, position }, index) => (
        <Fragment key={position}>
          <Typography sx={{ mt: 2 }} variant="body1">
            {position}
          </Typography>
          <TextField
            error={!!validation[index]}
            fullWidth
            helperText={validation[index]}
            label="Volume"
            onChange={event => handleChange(index, event.target.value)}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">gal</InputAdornment>,
              },
            }}
            value={capacityInGallons.toLocaleString()}
          />
          <Box sx={{ mt: 0.5 }}>
            <IconButton
              disabled={compartments.length <= 1}
              onClick={() => onRemoveCompartment(index)}
            >
              <ClearIcon />
            </IconButton>
          </Box>
        </Fragment>
      ))}
      <Button
        onClick={onAddCompartment}
        startIcon={<AddIcon />}
        sx={{ gridColumn: '2/3' }}
        variant="text"
      >
        Add Compartment
      </Button>
    </Box>
  );
};

CompartmentsForm.propTypes = {
  compartments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onAddCompartment: PropTypes.func.isRequired,
  onRemoveCompartment: PropTypes.func.isRequired,
  onUpdateCompartment: PropTypes.func.isRequired,
  title: PropTypes.string,
};
CompartmentsForm.defaultProps = {
  title: undefined,
};

export default CompartmentsForm;
